// Улирлын сурлагын тайлан (/study/report?s=&t=) — хэвлэхэд бэлэн A4 хуудас; "PDF татах" = хөтчийн Save as PDF.
// Зөвхөн багш нийтэлсэн хичээлүүд орно.
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Spin } from "antd";
import { FiArrowLeft, FiDownload } from "react-icons/fi";
import dayjs from "dayjs";
import logo from "assets/images/logo.jpg";
import { get, base, levelLabel, MARK } from "./shared";

const PRINT_CSS = `
@media print {
  @page { size: A4; margin: 14mm; }
  body * { visibility: hidden; }
  #study-report, #study-report * { visibility: visible; }
  #study-report { position: absolute; left: 0; top: 0; width: 100%; }
  .no-print { display: none !important; }
  .avoid-break { break-inside: avoid; }
}`;

export default function StudyReport() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const who = params.get("s") || "me";
  const term = params.get("t") || undefined;
  const [d, setD] = useState(null);
  useEffect(() => { get(`${base(who)}/report`, term ? { termId: term } : {}).then(setD).catch(() => setD({ error: true })); }, [who, term]);

  if (!d) return <div className="flex justify-center py-16"><Spin /></div>;
  if (d.error) return <div className="pt-8 max-w-3xl mx-auto text-sm text-gray-500">Тайлан татаж чадсангүй.</div>;
  const s = d.student;
  const totals = d.courses.map((c) => c.grade?.total).filter((v) => v != null);
  const termAvg = totals.length ? totals.reduce((n, v) => n + v, 0) / totals.length : null;

  return (
    <div className="pt-4 md:pt-8 pb-10 max-w-[820px] mx-auto w-full flex flex-col gap-4">
      <style>{PRINT_CSS}</style>
      <div className="no-print flex items-center justify-between gap-3">
        <button type="button" onClick={() => nav(-1)} className="text-sm text-primary flex items-center gap-1 bg-transparent border-0 p-0 cursor-pointer"><FiArrowLeft /> Буцах</button>
        <button type="button" onClick={() => window.print()} className="pchip pchip-on !h-10 flex items-center gap-1.5"><FiDownload /> PDF татах / хэвлэх</button>
      </div>
      <p className="no-print m-0 text-xs text-gray-500">"PDF татах" дарахад гарах цонхноос "Save as PDF" (PDF болгон хадгалах)-ыг сонгоно.</p>

      <article id="study-report" className="bg-white rounded-2xl border border-[#e9e8f0] p-6 md:p-8 flex flex-col gap-5 text-[#1c1c1c]">
        <header className="flex items-center gap-4 border-b border-[#e5d8f5] pb-4">
          <img src={logo} alt="" className="h-14 w-14 object-contain" />
          <div className="flex-1">
            <div className="text-xs font-bold uppercase tracking-wide text-[#6e5e8a]">Томүжин Алтернатив Сургууль</div>
            <h1 className="m-0 text-xl font-extrabold text-primary">Сурлагын тайлан · {d.term.name}</h1>
            <div className="text-sm text-gray-600">{d.term.schoolYear} хичээлийн жил</div>
          </div>
          <div className="text-right">
            <div className="text-[11px] text-gray-500">Дундаж</div>
            <div className="num font-extrabold text-3xl text-primary">{termAvg == null ? "—" : `${Math.round(termAvg)}%`}</div>
            <div className="text-[11px] text-gray-500">{totals.length} хичээл</div>
          </div>
        </header>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
          <div><div className="text-[11px] text-gray-500">Сурагч</div><b>{s.lastName} {s.firstName}</b></div>
          <div><div className="text-[11px] text-gray-500">Код</div><b>{s.code}</b></div>
          <div><div className="text-[11px] text-gray-500">Анги</div><b>{s.grade}-р анги</b></div>
          <div><div className="text-[11px] text-gray-500">Хаус</div><b>{s.house || "—"}</b></div>
        </div>

        {!d.courses.length && <p className="m-0 text-sm text-gray-500">Энэ улиралд багш нар дүнгээ хараахан нийтлээгүй байна.</p>}
        {d.courses.map((c) => (
          <section key={c.courseId} className="avoid-break flex flex-col gap-2 border-t border-[#f0edf5] pt-4">
            <div className="flex items-baseline justify-between gap-3">
              <h2 className="m-0 text-base font-bold">{c.subject}</h2>
              <span className="text-sm">{c.grade?.total != null ? <span className="font-bold tabular-nums text-primary text-base">{Math.round(c.grade.total)}%</span> : <b className="tabular-nums">{c.avg == null ? "—" : c.avg.toFixed(1)}</b>}</span>
            </div>
            {c.grade && <div className="text-[12px] text-gray-600">Суралцахуйн үр дүн {c.grade.sydPct == null ? "—" : `${Math.round(c.grade.sydPct)}%`} · Шалгалт {c.grade.examPct == null ? "—" : `${Math.round(c.grade.examPct)}%`} · Ирц {c.grade.attendancePct == null ? "—" : `${Math.round(c.grade.attendancePct)}%`}</div>}
            <table className="w-full text-[13px] border-collapse">
              <tbody>
                {c.outcomes.map((o) => (
                  <tr key={o.id} className="border-b border-[#f6f3fa] align-top">
                    <td className="py-1.5 pr-3">{o.code ? <b className="text-primary mr-1">{o.code}</b> : null}{o.text}
                      {o.criteria.some((x) => x.level != null || x.mark) && (
                        <div className="text-[11px] text-gray-500 mt-0.5">{o.criteria.filter((x) => x.level != null || x.mark).map((x) => `${x.text} — ${x.level ?? MARK[x.mark] ?? x.mark}`).join(" · ")}</div>
                      )}
                    </td>
                    <td className="py-1.5 w-[90px] text-right whitespace-nowrap"><b className="tabular-nums">{o.level.toFixed(1)}</b> <span className="text-[11px] text-gray-500">{levelLabel(o.level)}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
            {c.comment?.text && (
              <div className="flex gap-3 items-start bg-[#faf7ff] rounded-lg p-2.5 text-[13px]">
                {c.teacher && <img src={c.teacher.photo || c.teacher.demoPhoto} alt="" className="h-12 w-12 rounded-full object-cover bg-white border border-[#e5d8f5] shrink-0" />}
                <div className="flex-1 min-w-0 flex flex-col gap-1.5">
                  <b className="text-primary">{c.teacher ? `${c.teacher.name} багш — ` : ""}Багшийн зөвлөмж</b>
                  <span>{(who === "me" && c.comment.studentAuto) || c.comment.auto || c.comment.text}</span>
                  {c.comment.auto && c.comment.special && <span><b className="text-primary">Онцгойлон хэлэх зүйл:</b> {c.comment.special}</span>}
                </div>
              </div>
            )}
          </section>
        ))}
        <footer className="border-t border-[#e5d8f5] pt-3 text-[11px] text-gray-500 flex justify-between">
          <span>Түвшин: 1 Эхлэл · 2 Хөгжиж буй · 3 Хангасан · 4 Давсан</span>
          <span>{dayjs(d.generatedAt).format("YYYY-MM-DD")}</span>
        </footer>
      </article>
    </div>
  );
}
